/**
 * inkya.js — Matching catalogue interne FG-OS <-> catalogue concurrent Inkya.
 *
 * Inkya = imprim-encre.com (notre grossiste, site B2C). Prix affiches = TTC
 * CLIENT FINAL. Sert de comparateur : "cette ref, X % moins cher via nous".
 *
 * Strategie de match (par ordre de confiance) :
 *   1. EAN exact (13 chiffres) si present des 2 cotes.
 *   2. ref_interne normalisee == ref_inkya normalisee.
 *   3. ref_interne incluse dans ref_inkya (ou inverse) apres normalisation.
 *
 * Index construit une seule fois par import (Map), lookup O(1).
 */

/**
 * Normalise une reference pour comparaison :
 *   - upper
 *   - retire tout sauf [A-Z0-9]
 * Ex : "GG_HL280X-S" -> "GGHL280XS", "GG-HL 280X" -> "GGHL280X"
 */
export function normalizeRef(ref) {
  return String(ref || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Construit un index de lookup depuis le catalogue Inkya importe.
 * @param {Array} inkyaRefs
 * @returns {{ byEan: Map, byRef: Map, all: Array }}
 */
export function buildInkyaIndex(inkyaRefs) {
  const byEan = new Map();
  const byRef = new Map();
  const all = Array.isArray(inkyaRefs) ? inkyaRefs : [];
  for (const r of all) {
    if (r.ean && /^\d{13}$/.test(String(r.ean))) {
      byEan.set(String(r.ean), r);
    }
    const nr = normalizeRef(r.ref_inkya);
    if (nr && !byRef.has(nr)) byRef.set(nr, r);
  }
  return { byEan, byRef, all };
}

/**
 * Cherche la meilleure correspondance Inkya pour une ref interne FG-OS.
 *
 * @param {{ ref_interne?: string, ean?: string }} item  ligne catalogue FG-OS
 * @param {{ byEan: Map, byRef: Map, all: Array }} index  sortie buildInkyaIndex
 * @returns {{ match: object, method: 'ean'|'ref-exact'|'ref-partial' } | null}
 */
export function findInkyaMatch(item, index) {
  if (!index) return null;
  // 1. EAN exact
  if (item.ean && /^\d{13}$/.test(String(item.ean))) {
    const hit = index.byEan.get(String(item.ean));
    if (hit) return { match: hit, method: 'ean' };
  }
  // 2. ref exacte normalisee
  const nr = normalizeRef(item.ref_interne);
  if (!nr) return null;
  if (index.byRef.has(nr)) {
    return { match: index.byRef.get(nr), method: 'ref-exact' };
  }
  // 3. inclusion partielle (un cote contient l'autre), longueur min 5 pour limiter le bruit
  if (nr.length >= 5) {
    for (const [k, v] of index.byRef) {
      if (k.length >= 5 && (k.includes(nr) || nr.includes(k))) {
        return { match: v, method: 'ref-partial' };
      }
    }
  }
  return null;
}

/**
 * Calcule l'argument prix vs Inkya pour une ligne.
 *
 * @param {number} prixNotreVente  notre prix de vente HT au client (ligne devis)
 * @param {object} inkyaMatch       objet ref Inkya (prix_ht_estime / prix_ttc)
 * @returns {{
 *   inkyaHT: number, inkyaTTC: number,
 *   ecartHT: number, pourcentage: number, moinsCher: boolean
 * } | null}
 *
 * Compare notre HT au HT client final Inkya (prix_ht_estime).
 * moinsCher = true si on est sous le prix Inkya (argument de vente).
 */
export function compareToInkya(prixNotreVente, inkyaMatch) {
  if (!inkyaMatch) return null;
  const inkyaHT = Number(inkyaMatch.prix_ht_estime) || 0;
  const inkyaTTC = Number(inkyaMatch.prix_ttc) || 0;
  if (inkyaHT <= 0) return { inkyaHT, inkyaTTC, ecartHT: 0, pourcentage: 0, moinsCher: false };
  const ecartHT = inkyaHT - prixNotreVente;
  const pourcentage = (ecartHT / inkyaHT) * 100;
  return {
    inkyaHT,
    inkyaTTC,
    ecartHT,
    pourcentage,
    moinsCher: ecartHT > 0,
  };
}
